import React, { useState, useEffect } from 'react';
import { 
  User, CheckCircle, AlertCircle, RefreshCw, 
  Edit, Save, Phone, Home, Briefcase, Fingerprint, Smartphone, Trash2, Calendar
} from 'lucide-react';
import { api } from '../utils/api';
import Swal from 'sweetalert2';

import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';

const academicPositions = [
  'ครูผู้ช่วย',
  'ครู',
  'ครูชำนาญการ',
  'ครูชำนาญการพิเศษ',
  'ครูเชี่ยวชาญ',
  'ครูเชี่ยวชาญพิเศษ',
  'ครูอัตราจ้าง',
  'ลูกจ้าง(ชั่วคราว)',
];

const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let charCode of bytes) {
    str += String.fromCharCode(charCode);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const MyProfile = ({ user }) => {
  const [employee, setEmployee] = useState(null);
  const [editedEmployee, setEditedEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [devices, setDevices] = useState([]); // เก็บรายการอุปกรณ์
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [registering, setRegistering] = useState(false);
  const maxDevices = 2; // จำกัดสูงสุด 2 เครื่อง

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // ดึงข้อมูล พนักงาน, แผนก และ อุปกรณ์(Passkey) พร้อมกัน
      const [empData, deptData, passkeyData] = await Promise.all([
        api.getEmployees(),
        api.getDepartments(),
        api.getPasskeys(user.employee_id)
      ]);
      
      const currentEmployee = empData.find(emp => emp.employee_id === user.employee_id);
      
      if (currentEmployee) {
        setEmployee(currentEmployee);
        setEditedEmployee(currentEmployee);
      }
      setDepartments(deptData);
      
      if (passkeyData && passkeyData.success) {
        setDevices(passkeyData.devices || []);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลได้: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedEmployee({ ...editedEmployee, [name]: value });
  };

  const handleSaveProfile = async () => {
    if (!editedEmployee) return;
    const updateData = {
      employee_id: editedEmployee.employee_id,
      first_name: editedEmployee.first_name,
      last_name: editedEmployee.last_name,
      phone: editedEmployee.phone,
      address: editedEmployee.address,
      academic_position: editedEmployee.academic_position,
      department: editedEmployee.department,
    };

    try {
      const data = await api.updateEmployee(updateData);
      if (data.success) {
        setEmployee(editedEmployee);
        setIsEditing(false);
        Swal.fire('สำเร็จ', 'บันทึกข้อมูลเรียบร้อยแล้ว', 'success');
      } else {
        Swal.fire('ข้อผิดพลาด', 'ไม่สามารถบันทึกข้อมูลได้: ' + data.error, 'error');
      }
    } catch (error) {
      Swal.fire('ข้อผิดพลาด', 'เกิดข้อผิดพลาด: ' + error.message, 'error');
    }
  };

  const handleRegisterDevice = async () => {
    if (!employee || registering) return;
    
    if (devices.length >= maxDevices) {
      Swal.fire('โควต้าเต็ม', `คุณลงทะเบียนอุปกรณ์ครบ ${maxDevices} เครื่องแล้ว กรุณาลบเครื่องเก่าออกก่อน`, 'warning');
      return;
    }

    setRegistering(true);

    try {
      if (Capacitor.isNativePlatform()) {
        const check = await NativeBiometric.isAvailable();
        if (!check.isAvailable) throw new Error('DEVICE_NOT_SECURE');
        
        try {
          await NativeBiometric.verifyIdentity({
            reason: "ยืนยันตัวตนเพื่อลงทะเบียนอุปกรณ์นี้",
            title: "ลงทะเบียนอุปกรณ์",
          });
        } catch (verifyError) {
          throw new Error('USER_CANCELLED');
        }

        const credentialData = {
          employee_id: employee.employee_id,
          credential_id: 'native_device_' + employee.employee_id + '_' + Date.now(),
          public_key: 'native_auth_bypass', 
          client_data: 'capacitor_app'
        };
        await api.savePasskey(credentialData);
      } else {
        if (!window.PublicKeyCredential) {
          throw new Error('BROWSER_NOT_SUPPORTED');
        }

        const options = await api.getWebAuthnRegistrationOptions(employee.employee_id);
        options.challenge = base64urlToBuffer(options.challenge);
        options.user.id = base64urlToBuffer(options.user.id);

        const credential = await navigator.credentials.create({ publicKey: options });
        const credentialData = {
          employee_id: employee.employee_id,
          credential_id: credential.id,
          public_key: bufferToBase64url(credential.response.attestationObject), 
          client_data: bufferToBase64url(credential.response.clientDataJSON)
        };
        await api.savePasskey(credentialData);
      }

      localStorage.setItem('device_bound_to_employee', employee.employee_id);
      Swal.fire('สำเร็จ', 'ลงทะเบียนอุปกรณ์นี้สำเร็จ!', 'success');
      loadData(); // รีโหลดรายการอุปกรณ์ใหม่
      
    } catch (error) {
      console.error('Registration failed:', error);
      
      let errorMessage = 'การลงทะเบียนถูกยกเลิก หรืออุปกรณ์ของคุณไม่รองรับ';
      if (error.message === 'DEVICE_NOT_SECURE') {
        errorMessage = 'อุปกรณ์ยังไม่ได้ตั้งค่าความปลอดภัย กรุณาไปตั้งค่ารหัสล็อคหน้าจอหรือลายนิ้วมือในเครื่องก่อน';
      } else if (error.message === 'USER_CANCELLED' || error.name === 'NotAllowedError') {
        errorMessage = 'คุณยกเลิกการทำรายการ';
      } else if (error.message === 'BROWSER_NOT_SUPPORTED') {
        errorMessage = 'อุปกรณ์หรือบราวเซอร์นี้ไม่รองรับระบบ Passkey';
      }

      Swal.fire('ยกเลิก', errorMessage, 'error');
    } finally {
      setRegistering(false);
    }
  };

  const handleDeleteDevice = async (id, deviceName) => {
    const confirm = await Swal.fire({
      title: 'ลบอุปกรณ์?',
      text: `คุณต้องการลบอุปกรณ์ "${deviceName}" ใช่หรือไม่?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบข้อมูล',
      cancelButtonText: 'ยกเลิก'
    });

    if (confirm.isConfirmed) {
      try {
        const response = await api.deletePasskey(id);
        if (response.success) {
          Swal.fire('ลบแล้ว', 'ลบอุปกรณ์เรียบร้อยแล้ว', 'success');
          loadData(); 
        } else {
          Swal.fire('ผิดพลาด', 'ไม่สามารถลบได้: ' + response.error, 'error');
        }
      } catch (error) {
        Swal.fire('ผิดพลาด', 'เกิดข้อผิดพลาด: ' + error.message, 'error');
      }
    }
  };

  if (loading && !employee) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">ข้อมูลของฉัน</h1>
        <p className="text-gray-600 mt-2">จัดการข้อมูลส่วนตัวและอุปกรณ์สำหรับลงเวลาทำงาน</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Info Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <User className="h-6 w-6 mr-2 text-blue-500" /> ข้อมูลบุคลากร
            </h2>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="text-blue-500 hover:text-blue-700 text-sm font-medium flex items-center space-x-1">
                <Edit className="h-4 w-4" /><span>แก้ไข</span>
              </button>
            )}
          </div>
          
          <div className="p-6 space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-10 w-10 text-blue-500" />
                </div>
              </div>
              
              <div className="flex-1 space-y-3">
                {!isEditing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-sm text-gray-500">รหัสบุคลากร</label><p className="text-lg font-semibold text-gray-900">{employee?.employee_id}</p></div>
                    <div><label className="text-sm text-gray-500">ชื่อ-นามสกุล</label><p className="text-lg font-semibold text-gray-900">{employee?.first_name} {employee?.last_name}</p></div>
                    <div><label className="text-sm text-gray-500 flex items-center"><Phone className="h-4 w-4 mr-1 text-gray-400"/> เบอร์โทรศัพท์</label><p className="text-lg font-semibold text-gray-900">{employee?.phone || '-'}</p></div>
                    <div><label className="text-sm text-gray-500 flex items-center"><Briefcase className="h-4 w-4 mr-1 text-gray-400"/> ตำแหน่ง</label><p className="text-lg font-semibold text-gray-900">{employee?.academic_position || '-'}</p></div>
                    <div className="md:col-span-2"><label className="text-sm text-gray-500 flex items-center"><Home className="h-4 w-4 mr-1 text-gray-400"/> ที่อยู่</label><p className="text-base text-gray-900">{employee?.address || '-'}</p></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm text-gray-700 mb-1">รหัสพนักงาน</label><input type="text" value={editedEmployee?.employee_id} disabled className="w-full px-3 py-2 border rounded-md bg-gray-100" /></div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">ตำแหน่ง</label>
                      <select name="academic_position" value={editedEmployee?.academic_position || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md">
                        <option value="">-- ไม่ระบุ --</option>
                        {academicPositions.map(pos => (<option key={pos} value={pos}>{pos}</option>))}
                      </select>
                    </div>
                    <div><label className="block text-sm text-gray-700 mb-1">ชื่อ</label><input type="text" name="first_name" value={editedEmployee?.first_name} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div><label className="block text-sm text-gray-700 mb-1">นามสกุล</label><input type="text" name="last_name" value={editedEmployee?.last_name} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md" /></div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-700 mb-1">ที่อยู่ (ย่อ)</label>
                      <textarea rows="2" name="address" value={editedEmployee?.address || ''} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md"></textarea>
                    </div>
                  </div>
                )}
                
                {isEditing ? (
                  <div className="pt-3 border-t flex space-x-3 justify-end">
                    <button onClick={() => setIsEditing(false)} className="bg-gray-200 py-2 px-4 rounded-md">ยกเลิก</button>
                    <button onClick={handleSaveProfile} className="bg-blue-500 text-white py-2 px-4 rounded-md flex items-center"><Save className="h-4 w-4 mr-2"/>บันทึก</button>
                  </div>
                ) : (
                  <div className="pt-3 border-t">
                    <label className="text-sm text-gray-500">สถานะอุปกรณ์ (Passkey)</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        devices.length > 0
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {devices.length > 0 ? (
                          <CheckCircle className="h-4 w-4 mr-1" />
                        ) : (
                          <AlertCircle className="h-4 w-4 mr-1" />
                        )}
                        {devices.length > 0 ? `ลงทะเบียนแล้ว ${devices.length}/${maxDevices} เครื่อง` : 'ยังไม่มีอุปกรณ์ที่ลงทะเบียน'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Passkey Management Card */}
        <div className="bg-white rounded-lg shadow flex flex-col">
          <div className="px-6 py-4 bg-gray-50 border-b">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Smartphone className="h-6 w-6 mr-2 text-blue-500" /> จัดการอุปกรณ์ ({devices.length}/{maxDevices})
            </h2>
            <p className="text-sm text-gray-600 mt-1">ผูกใบหน้าหรือลายนิ้วมือเข้ากับอุปกรณ์เพื่อความปลอดภัย</p>
          </div>
          
          <div className="p-6 flex-1 flex flex-col">
            
            {/* List of Devices */}
            <div className="flex-1 space-y-3 mb-6">
              {devices.length === 0 ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Smartphone className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">ยังไม่มีอุปกรณ์ที่ลงทะเบียน</p>
                </div>
              ) : (
                devices.map((device, idx) => (
                  <div key={device.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-blue-300 transition-all">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm w-40 sm:w-64 truncate" title={device.device_name || 'ไม่ระบุอุปกรณ์'}>
                          {device.device_name || 'อุปกรณ์ที่ไม่ได้ระบุชื่อ'}
                        </h4>
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Calendar className="h-3 w-3 mr-1" />
                          {new Date(device.created_at).toLocaleDateString('th-TH')}
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleDeleteDevice(device.id, device.device_name || `อุปกรณ์ที่ ${idx + 1}`)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="ลบอุปกรณ์นี้"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Action Button */}
            <button
              onClick={handleRegisterDevice}
              disabled={registering || devices.length >= maxDevices}
              className={`w-full py-3 px-4 rounded-xl flex items-center justify-center space-x-2 font-bold shadow-md transition-all ${
                devices.length >= maxDevices 
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {registering ? (
                <><RefreshCw className="h-5 w-5 animate-spin" /><span>กำลังดำเนินการ...</span></>
              ) : (
                <><Fingerprint className="h-5 w-5" /><span>{devices.length >= maxDevices ? 'โควต้าอุปกรณ์เต็มแล้ว' : 'ลงทะเบียนเครื่องนี้'}</span></>
              )}
            </button>
            
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default MyProfile;